import {
  PaginatedPresetTaskListReadable,
  PaginatedReturnUserListReadable,
  PaginatedTaskTypeListReadable,
  PresetTaskReadable,
  PresetTasksListData,
  presetTasksListInfiniteOptions,
  ReturnUserReadable,
  tasksCreateMutation,
  tasksPartialUpdateMutation,
  tasksRetrieveOptions,
  taskStatusesListOptions,
  TaskTypeReadable,
  TaskTypesListData,
  taskTypesListInfiniteOptions,
  UsersListData,
  usersListInfiniteOptions
} from '@crm/api'
import { InfiniteSelect } from '@crm/components'
import { TaskStatus } from '@crm/features/tasks/components'
import {
  defaultNonPagination,
  formatDateToDayjs,
  formatToIsoString,
  getSelectIdValue,
  listTransformResults,
  TagPriorityByValue,
  userTransformResults
} from '@crm/utils'
import { useSessionContext } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import {
  DATE_TIME_FORMAT,
  DATE_TIME_FORMAT_API,
  format,
  showApiErrors,
  tomorrow,
  transformOptions
} from '@shared/utils'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { App, Col, DatePicker, Form, Input, Radio, Row, Tag } from 'antd'
import { useEffect } from 'react'
import { useParams } from 'react-router-dom'

import { useStyles } from './styles'

const DEFAULT_STATUS_CODE = 'to-do'

type Props = {
  id?: string | number
  formName?: string
  onCancel: () => void
  readOnly?: boolean
  duplicate?: boolean
}

export const TaskForm = ({
  formName,
  id,
  onCancel,
  readOnly = false,
  duplicate = false
}: Props) => {
  const { styles } = useStyles()
  const queryClient = useQueryClient()
  const { t } = useLocales()
  const { id: lead } = useParams()
  const [form] = Form.useForm()
  const { user } = useSessionContext()
  const { message } = App.useApp()

  const { data } = useQuery({
    ...tasksRetrieveOptions({ path: { id: Number(id) } }),
    enabled: !!id,
    refetchOnMount: 'always'
  })

  const { data: taskStatuses } = useQuery(
    taskStatusesListOptions({
      query: { per_page: Number(defaultNonPagination.per_page) }
    })
  )
  const preselectedStatusId = taskStatuses?.results.find(
    ({ code }) => code === DEFAULT_STATUS_CODE
  )?.id

  useEffect(() => {
    if (data) {
      const {
        owner,
        type,
        deadline,
        opportunity: _opportunity,
        ...formData
      } = data

      form.setFieldsValue({
        ...formData,
        owner: owner?.id,
        type: {
          value: type?.id,
          label: type?.name
        },
        status: formData?.status?.id || preselectedStatusId,
        ...(deadline && { deadline: formatDateToDayjs(deadline) })
      })
    } else {
      form.setFieldsValue({
        owner: user?.user?.id,
        priority: 2,
        deadline: tomorrow,
        status: preselectedStatusId
      })
    }
  }, [data, form, lead, user?.user, preselectedStatusId])

  const onMutate = () => {
    queryClient.refetchQueries()
    onCancel()
    message.success(t('success.success_data_save'))
  }

  const { mutate: createMutate } = useMutation({
    ...tasksCreateMutation(),
    onSuccess: () => onMutate(),
    onError: (e) => showApiErrors(e, form)
  })

  const { mutate: editMutate } = useMutation({
    ...tasksPartialUpdateMutation(),
    onSuccess: () => onMutate(),
    onError: (e) => showApiErrors(e, form)
  })

  const onSubmit = async () => {
    const values = await form.validateFields()
    const { deadline, owner, type, completed, ...rest } = values

    const requestBody = {
      ...rest,
      ...(lead && { lead }),
      owner: getSelectIdValue(
        owner,
        data?.owner?.id ?? user?.user.sso_id ?? undefined
      ),
      type: getSelectIdValue(Number(type) ? type : type?.value, data?.type?.id),
      ...(completed && { completed_at: formatToIsoString(new Date()) }),
      deadline:
        deadline &&
        formatToIsoString(
          format(deadline, DATE_TIME_FORMAT_API, DATE_TIME_FORMAT)
        )
    }

    if (id && !duplicate) {
      editMutate({ path: { id: Number(id) }, body: requestBody })
    } else {
      createMutate({ body: requestBody })
    }
  }

  const onSelectTemplate = (_: number, option: PresetTaskReadable) => {
    if (option) {
      form.setFieldsValue({
        title: option?.title,
        description: option?.description,
        priority: option?.priority,
        type: {
          value: option?.type?.id,
          label: option?.type?.name
        }
      })
    }
  }

  return (
    <Form form={form} name={formName} onFinish={onSubmit} layout="vertical">
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item label={t('task.template')}>
            <InfiniteSelect<
              PresetTasksListData,
              PaginatedPresetTaskListReadable,
              PresetTaskReadable
            >
              className="w-full"
              labelInValue
              popupMatchSelectWidth
              placeholder={t('task.select_template')}
              getQueryFunction={presetTasksListInfiniteOptions}
              disabled={readOnly}
              onSelect={(_, option) =>
                onSelectTemplate(_, option as PresetTaskReadable)
              }
              getOptions={({ options }) =>
                transformOptions({
                  options,
                  label: 'title',
                  value: 'id',
                  returnOption: true
                })
              }
            />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item
            name="title"
            label={t('task.title')}
            rules={[{ required: true }]}
          >
            <Input placeholder={t('task.title')} disabled={readOnly} />
          </Form.Item>
        </Col>
      </Row>

      <Row>
        <Col span={24}>
          <Form.Item name="description" label={t('task.description')}>
            <Input.TextArea
              rows={4}
              placeholder={t('task.description')}
              disabled={readOnly}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={12}>
        <Col span={12}>
          <Form.Item name="owner" label={t('task.assign_to')}>
            <InfiniteSelect<
              UsersListData,
              PaginatedReturnUserListReadable,
              ReturnUserReadable
            >
              className="w-full"
              popupMatchSelectWidth
              placeholder={t('main.select')}
              getQueryFunction={usersListInfiniteOptions}
              disabled={readOnly}
              getOptions={({ options }) => userTransformResults(options)}
            />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item name="status" label={t('nomenclature.status')}>
            <TaskStatus />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={12}>
        <Col span={12}>
          <Form.Item name="type" label={t('task.task_type')}>
            <InfiniteSelect<
              TaskTypesListData,
              PaginatedTaskTypeListReadable,
              TaskTypeReadable
            >
              className="w-full"
              popupMatchSelectWidth
              placeholder={t('task.task_type')}
              getQueryFunction={taskTypesListInfiniteOptions}
              disabled={readOnly}
              getOptions={({ options }) => listTransformResults(options)}
            />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item name="priority" label={t('task.priority')}>
            <Radio.Group
              className={styles.radioGroup}
              size="small"
              options={Object.entries(TagPriorityByValue(t)).map(
                ([key, value]) => ({
                  label: <Tag color={value.color}>{value.title}</Tag>,
                  value: Number(key)
                })
              )}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={12}>
        <Col span={12}>
          <Form.Item name="deadline" label={t('filter.deadline')}>
            <DatePicker
              placement="bottomRight"
              showTime
              format={DATE_TIME_FORMAT}
              disabled={readOnly}
              className="w-full"
            />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  )
}
